import { type FC } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useTranslation } from "react-i18next";
import { ICON_MAP } from "@/shared/components/Player/timecodeIcons";
import { secondsToTimeString } from "./timeUtils";
import type { EditableTimecode } from "./types";

// Порт референсного VideoForm/Timecodes/TimecodesList.js

const ContentContainer = styled("div")({
  maxHeight: "70vh",
  overflow: "auto",
  paddingBottom: 16,
});

const StyledListItem = styled(ListItem)(({ theme }) => ({
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const IconContainer = styled("div")({
  width: 40,
  height: 40,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "2px solid white",
  "& svg": {
    fontSize: 22,
    color: "white",
  },
});

interface TimecodesListProps {
  timecodes: EditableTimecode[];
  loading?: boolean;
  onClickEdit: (index: number) => void;
  onTimecodeClick: (time: number) => void;
  onDeleteTimecode: (index: number) => void;
  onCreateTimecode: () => void;
  videoDuration?: number;
}

export const TimecodesList: FC<TimecodesListProps> = ({
  timecodes,
  loading,
  onClickEdit,
  onTimecodeClick,
  onDeleteTimecode,
  onCreateTimecode,
  videoDuration = 0,
}) => {
  const { t } = useTranslation();

  const useHours = videoDuration >= 3600;

  const handleDelete = (index: number) => {
    if (window.confirm(t("videos.timecodes.confirmDelete"))) {
      onDeleteTimecode(index);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="300px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!timecodes.length) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="300px"
        flexDirection="column"
        gap={2}
        p={3}
      >
        <Typography variant="h6" color="textSecondary">
          {t("videos.timecodes.noTimecodes")}
        </Typography>
        <Typography variant="body2" color="textSecondary" textAlign="center">
          {t("videos.timecodes.addFirstTimecode")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={onCreateTimecode}
          sx={{ mt: 1 }}
        >
          {t("videos.timecodes.create")}
        </Button>
      </Box>
    );
  }

  return (
    <div>
      <Box p={2}>
        <Typography variant="h6" gutterBottom>
          {t("videos.timecodes.list")} ({timecodes.length})
        </Typography>
      </Box>

      <ContentContainer>
        <List disablePadding>
          {timecodes.map((timecode, index) => {
            const IconComponent = ICON_MAP[timecode.icon] ?? PlayArrowIcon;
            const isAnnotation = timecode.type === "ANNOTATION";

            return (
              <StyledListItem
                key={timecode.id ?? `${timecode.time}-${index}`}
                onClick={() => onTimecodeClick(timecode.time)}
                secondaryAction={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={secondsToTimeString(timecode.time, useHours)}
                      size="small"
                      sx={{
                        minWidth: useHours ? 80 : 60,
                        fontFamily: "monospace",
                      }}
                    />
                    <Tooltip title={t("common.actions.edit")}>
                      <IconButton
                        onClick={(event) => {
                          event.stopPropagation();
                          onClickEdit(index);
                        }}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.actions.delete")}>
                      <IconButton
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDelete(index);
                        }}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                }
              >
                <ListItemIcon>
                  <IconContainer>
                    <IconComponent />
                  </IconContainer>
                </ListItemIcon>

                <ListItemText
                  primary={
                    <Stack spacing={1} direction="row" alignItems="center">
                      <Typography variant="subtitle1" component="span">
                        {timecode.title}
                      </Typography>
                      <Chip
                        label={
                          isAnnotation
                            ? t("videos.timecodes.annotation")
                            : t("videos.timecodes.marker")
                        }
                        size="small"
                        color={isAnnotation ? "secondary" : "primary"}
                      />
                    </Stack>
                  }
                  secondary={
                    !isAnnotation && timecode.description ? (
                      <Typography variant="body2" color="textSecondary">
                        {timecode.description}
                      </Typography>
                    ) : null
                  }
                />
              </StyledListItem>
            );
          })}

          <ListItem>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateTimecode}
              sx={{ mt: 1 }}
            >
              {t("videos.timecodes.create")}
            </Button>
          </ListItem>
        </List>
      </ContentContainer>
    </div>
  );
};
